import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { products } from '../../mocks/products';
import { useSEO } from '../../utils/seo';

export default function Store() {
  const categories = ['All', 'Journals', 'Faith', 'Fitness', 'Food', 'Finances', 'Bundles', 'Home Decor'];

  // SEO Configuration
  useSEO({
    title: 'Store - Family Habit Tools, Journals & Resources | Rooted & Rising',
    description: 'Shop carefully curated tools and resources to support your family\'s micro-habit journey. Journals, faith-based items, fitness tools, and family activity products.',
    keywords: 'family habit tools, habit journals, faith-based products, family fitness tools, family resources, intentional living products',
    canonical: '/store',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Rooted & Rising Store Products',
      description: 'Carefully curated tools and resources to support your family\'s micro-habit journey',
      url: `${import.meta.env.VITE_SITE_URL || ''}/store`,
      numberOfItems: products.length,
      itemListElement: products.map((product, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Product',
          name: product.name,
          description: product.description,
          offers: {
            '@type': 'Offer',
            price: product.price,
            priceCurrency: 'USD',
            availability: 'https://schema.org/InStock'
          }
        }
      }))
    }
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 bg-gradient-to-br from-amber-50 to-rose-50">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            The Store
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            Carefully curated tools and resources to support your family's micro-habit journey
          </p>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-24 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-product-shop>
            {products.map((product) => (
              <div 
                key={product.id}
                className="bg-white border-2 border-gray-100 rounded-xl overflow-hidden hover:border-amber-200 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 cursor-pointer"
              >
                <div className="relative h-64 w-full">
                  <img 
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <span className="text-xs text-gray-500 uppercase tracking-wide">{product.category}</span>
                  <h3 className="text-lg font-semibold text-gray-900 mt-2 mb-3">{product.name}</h3>
                  <p className="text-sm text-gray-600 mb-4 leading-relaxed">{product.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold text-amber-600">${product.price}</span>
                    <button className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors whitespace-nowrap">
                      Add to Cart
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-4 bg-gradient-to-br from-rose-50 to-amber-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-amber-500 rounded-full mx-auto mb-4">
                <i className="ri-truck-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Free Shipping</h3>
              <p className="text-gray-600 text-sm">On orders over $50</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-rose-500 rounded-full mx-auto mb-4">
                <i className="ri-shield-check-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Quality Guaranteed</h3>
              <p className="text-gray-600 text-sm">30-day money back guarantee</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-emerald-500 rounded-full mx-auto mb-4">
                <i className="ri-heart-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Family Approved</h3>
              <p className="text-gray-600 text-sm">Tested by real families</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Not Sure Where to Start?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Explore our blog for guidance on building micro-habits and choosing the right tools for your family
          </p>
          <Link 
            to="/blog" 
            className="inline-flex items-center px-8 py-4 bg-gray-900 text-white rounded-full font-medium hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
          >
            Read the Blog
            <i className="ri-arrow-right-line ml-2"></i>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}